/**
 * Client-side mirror of the backend's YouTube id extraction
 * (curriculum/blocks.py `extract_youtube_id`) — used for instant previews in
 * the editor. The server re-validates and stores ONLY the 11-char id.
 */

const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
]);

const YOUTUBE_PATH_PREFIXES = ['/embed/', '/shorts/', '/live/', '/v/'];

/** Extract the 11-char video id from a raw id or any YouTube URL shape;
 *  `null` when nothing valid can be extracted. */
export function extractYoutubeId(value: string): string | null {
  const raw = (value ?? '').trim();
  if (YOUTUBE_ID_RE.test(raw)) return raw;

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

  const host = url.hostname.toLowerCase();
  let candidate: string | null = null;
  if (host === 'youtu.be' || host === 'www.youtu.be') {
    candidate = url.pathname.replace(/^\//, '').split('/')[0] ?? null;
  } else if (YOUTUBE_HOSTS.has(host)) {
    if (url.pathname === '/watch') {
      candidate = url.searchParams.get('v');
    } else {
      for (const prefix of YOUTUBE_PATH_PREFIXES) {
        if (url.pathname.startsWith(prefix)) {
          candidate = url.pathname.slice(prefix.length).split('/')[0] ?? null;
          break;
        }
      }
    }
  }
  return candidate && YOUTUBE_ID_RE.test(candidate) ? candidate : null;
}

/** Privacy-enhanced embed URL — the ONLY YouTube URL the app ever renders. */
export function youtubeEmbedUrl(videoId: string): string {
  return `https://www.youtube-nocookie.com/embed/${videoId}`;
}
